import * as _ from 'lodash'
import {BehaviorSubject} from 'rxjs'
import {IProgressProvider} from '../PA.Core/PA.Core.IF.Basic';
import {ASync} from '../PA.Core/Basic/ASync'
import {ProgressProvider} from '../PA.Core/Basic/Progress'
import {PAdbContext} from '../PA.Core/PAdbContext';
import {esWB_Event, esWB_Product_Group} from "../PA.Core/PAdbSchema";
import {
  WB_EventRow,
  WB_PlanRow,
  WB_ProductGroupRow,
} from '../PA.Core/PAdbSchemaRows';
import {IHaveEvents, IHaveEventsListenerFunction} from "./PA.TPM.IF.Aspects";
import {EventBO, IEventCreateOptions, IEventLoadOptions} from "../PA.TPM.BusinessObjects/EventBO";
import {PlanBO, IPlanCreateOptions, IPlanLoadOptions} from "../PA.TPM.BusinessObjects/PlanBO";
import {PASearch} from "../PA.Core/PASearch";
import {BusinessObjectLoader} from "../PA.Core/BusinessObject/BusinessObjectLoader";


export interface EventCollectionOptions {
  dbContext?: PAdbContext,
  PlanCreateOptions?: IPlanCreateOptions,
  PlanLoadOptions?: IPlanLoadOptions,
  EventCreateOptions?: IEventCreateOptions,
  EventLoadOptions?: IEventLoadOptions
}

const EventCollectionOptions_default : EventCollectionOptions = {
  PlanCreateOptions: {bEagerLoadAspectInterfaces: true},
  PlanLoadOptions: {},
  EventCreateOptions: {bEagerLoadAspectInterfaces: false},
  EventLoadOptions: {}
}


export class EventCollection
  implements
    IHaveEvents
{
  _dbContext: PAdbContext
  _options: EventCollectionOptions

  _bIHaveEventsLoaded: boolean;
  _OnIHaveEventsLoaded: Promise<IHaveEvents>;

  _dctPlanBOQueuedById: { [id: number]: PlanBO }
  _dctPlanBOLoadedById: { [id: number]: PlanBO }

  _dctEventRowQueuedById: { [id: number]: WB_EventRow }
  _dctEventRowLoadedById: { [id: number]: WB_EventRow }

  _dctPrdGrpRowQueuedById: { [id: number]: WB_ProductGroupRow }
  _dctPrdGrpRowLoadedById: { [id: number]: WB_ProductGroupRow }
  _bLoadAllPrdGrpsQueued: boolean
  _bLoadAllPrdGrpsLoaded: boolean

  _bQueueLoadProcessRunning: boolean
  _bQueueLoadProcessPaused: boolean
  _arrLoadProcessingCompleteFunction: (()=> void)[];

  _dctEventById: { [id: number]: EventBO }
  _listeners: IHaveEventsListenerFunction[]
  _EventsChanged: BehaviorSubject<IHaveEvents>;
  _EventLoadProgressProvider: IProgressProvider;

  _PlanLoadPromise: Promise<IHaveEvents>

  _QueueChanged: BehaviorSubject<IHaveEvents>;


  constructor(options?: EventCollectionOptions) {

    this._options = {}
    _.defaults(this._options, options || {}, EventCollectionOptions_default)

    if ('dbContext' in this._options)
      this._dbContext = this._options.dbContext;
    else
      this._dbContext = undefined

    this._bIHaveEventsLoaded = false
    this._OnIHaveEventsLoaded = undefined

    this._dctPlanBOQueuedById = {}
    this._dctPlanBOLoadedById = {}

    this._dctEventRowQueuedById = {}
    this._dctEventRowLoadedById = {}

    this._dctPrdGrpRowQueuedById = {}
    this._dctPrdGrpRowLoadedById = {}
    this._bLoadAllPrdGrpsQueued = false
    this._bLoadAllPrdGrpsLoaded = false

    this._bQueueLoadProcessRunning = false
    this._bQueueLoadProcessPaused = false
    this._arrLoadProcessingCompleteFunction = []

    this._dctEventById = {}
    this._listeners = []
    this._EventsChanged = new BehaviorSubject<IHaveEvents>(this)
    this._EventLoadProgressProvider = new ProgressProvider("Event Loader", this)

    this._QueueChanged = new BehaviorSubject<IHaveEvents>(this)
    this._PlanLoadPromise = undefined
  }

  get dbContext() {
    return this._dbContext
  }
  set dbContext(value: PAdbContext) {
    // any validations.. ie.. no events can be loaded..
    this._dbContext = value
  }

  //<editor-fold desc="IHaveEvents operations">

  // Initial Loading
  get OnIHaveEventsLoaded(): Promise<IHaveEvents> {
    let self = this
    //
    if (this._bIHaveEventsLoaded) {
      return ASync.resolve(this)
    }

    if (!_.isEmpty(self._OnIHaveEventsLoaded)) {
      return self._OnIHaveEventsLoaded
    }

    self._OnIHaveEventsLoaded = new Promise<IHaveEvents>((resolve, error) => {
          resolve(self)
    })

    return self._OnIHaveEventsLoaded
  }
  get bIHaveEventsLoaded(): boolean {
    return this._bIHaveEventsLoaded
  }

  // Event Load Operations
  pauseLoadProcessing(): void {
      this._bQueueLoadProcessPaused = true
  }

  resumeLoadProcessing(): void {
    this._bQueueLoadProcessPaused = false
    this._IHaveEvents_processQueue()
  }

  resumeLoadProcessingWait(): Promise<void> {
    const self = this

    return new Promise<void>((resolve, error) => {
      self._arrLoadProcessingCompleteFunction.push(resolve)
      self.resumeLoadProcessing();
    })

  }



  // Event Operations
  hasEvent(aWB_Evt_ID: number): boolean {
    return ( aWB_Evt_ID in this._dctEventById)
  }
  getEvent(aWB_Evt_ID: number): EventBO {
    return this._dctEventById[aWB_Evt_ID];
  }
  addEvent(anEventBO: EventBO, bReplace?: boolean): void {
    if (_.isEmpty(anEventBO)) {
      return
    }

    if (!(anEventBO.bIsLoaded)) {
      return
    }

    if (bReplace || !this.hasEvent(anEventBO.drWB_Event.cif.WB_EVT_ID)) {
      this._dctEventById[anEventBO.drWB_Event.cif.WB_EVT_ID] = anEventBO
      this._notifyChange('add', [anEventBO], {})
    }
  }
  get Events(): EventBO[] {
    return _.values(this._dctEventById)
  }
  get EventsChanged(): BehaviorSubject<IHaveEvents> {
    return this._EventsChanged
  }

  addChangeListener(aListener: IHaveEventsListenerFunction): void {

    if (_.isEmpty(aListener)) {
      return
    }

    let found = false
    _.forEach(this._listeners, (element) => {
      if (element == aListener) {
        found = true
        return false
      }
    })

    if (!found) {
      this._listeners.push(aListener)
    }
  }
  removeChangeListener(aListener: IHaveEventsListenerFunction): void {
    _.remove(this._listeners, (element) => {
      return (element == aListener)
    })
  }


  // Plan Load Scope Operations
  hasPlan(aWBPlanId: number): boolean {
    return (aWBPlanId in this._dctPlanBOLoadedById)
  }
  getPlan(aWBPlanId: number): PlanBO {
    return (this._dctPlanBOLoadedById[aWBPlanId])
  }
  addPlanBO(aPlanBO: PlanBO): void {
    if(_.isEmpty(aPlanBO)) {
      return
    }
    this._dctPlanBOQueuedById[aPlanBO.drWB_Plan.cif.WB_PLAN_ID] = aPlanBO

    this._notifyQueueChange()
    this._IHaveEvents_processQueue()
  }
  addPlan(aWBPlanId: number): Promise<IHaveEvents> {

    let self = this
    let queuedPlan = self._dctPlanBOQueuedById[aWBPlanId]
    let loadedPlan = self._dctPlanBOLoadedById[aWBPlanId]

    if (!(_.isEmpty(queuedPlan) && _.isEmpty(loadedPlan))) {
      return Promise.resolve(self);
    }

    // If Loading, wait for existing Load to complete..
    if (!_.isEmpty(self._PlanLoadPromise)) {
      let aPlanLoadPromise = self._PlanLoadPromise
      let returnPromise = new Promise<IHaveEvents>((resolve, error) => {
        aPlanLoadPromise
          .then((result) => {
            return self.addPlan(aWBPlanId)
          })
          .then((result) => {
            resolve(self)
          })
          .catch((err) => {
            error(err)
          })
      })

      return returnPromise
    }


    self._PlanLoadPromise = new Promise<IHaveEvents>((resolve, error) => {
      self._options.PlanCreateOptions.dbContext = self.dbContext
      self._options.PlanCreateOptions.EventCollection = self
      let newPlanBO = new PlanBO(self._options.PlanCreateOptions)

      self._options.PlanLoadOptions.dbContext = self.dbContext
      newPlanBO.Load(aWBPlanId, self._options.PlanLoadOptions)
          .then((aLoadedBO: PlanBO) =>{

          self._PlanLoadPromise = undefined
          if (aLoadedBO.bIsLoaded) {
            self.addPlanBO(aLoadedBO)
          } else {
            self._IHaveEvents_processQueue()
          }
          resolve(self)
        })
        .catch((err) => {
          self._PlanLoadPromise = undefined
          console.log("Failed to Add/ Load Plan into Collection: " + err)
          error(err)
        })

    })

    return self._PlanLoadPromise
  }

  get Plans(): PlanBO[] {
    return _.values(this._dctPlanBOLoadedById)
  }

  loadEvents(arrEvtIds: number[]): void {
    let self = this

    let loadPromise = undefined
    if (_.isEmpty(arrEvtIds)) {
      loadPromise = ASync.resolve([])
    } else if (_.isArray(arrEvtIds)) {
      loadPromise = self.dbContext.findUsingCache(esWB_Event, arrEvtIds)
    } else {
      // Argument not array or empty
      return
    }

    loadPromise
        .then((arrWB_Event_Rows: WB_EventRow[]) => {

          if (_.isEmpty(arrWB_Event_Rows)) {
            return
          }

          _.forEach(arrWB_Event_Rows, (anEventRow: WB_EventRow)=> {
            if(anEventRow.cif.WB_EVT_ID in  self._dctEventRowQueuedById) {
              // Don't re-add it.
            } else {
              self._dctEventRowQueuedById[anEventRow.cif.WB_EVT_ID] = anEventRow
            }
          })
          self._notifyQueueChange()
          self._IHaveEvents_processQueue()

        })
        .catch((err) => {
          console.log(err)
          return
        })
  }


  // Load by Product Groups
  loadProductGroups(arrWBPGIds: number[]): void {
    let self = this

    let loadPromise = undefined
    if (_.isEmpty(arrWBPGIds)) {
      loadPromise = ASync.resolve([])
    } else if (_.isArray(arrWBPGIds)) {
      loadPromise = self.dbContext.findUsingCache(esWB_Product_Group, arrWBPGIds)
    } else {
      // Argument not array or empty
      return
    }

    loadPromise
      .then((arrWB_PrdGrp_Rows: WB_ProductGroupRow[]) => {

        if (_.isEmpty(arrWB_PrdGrp_Rows)) {
          return
        }

        _.forEach(arrWB_PrdGrp_Rows, (aWBPrdGrpRow: WB_ProductGroupRow)=> {
          if(aWBPrdGrpRow.cif.WB_PRDGRP_ID in  self._dctPrdGrpRowQueuedById) {
            // Don't re-add it.
          } else {
            self._dctPrdGrpRowQueuedById[aWBPrdGrpRow.cif.WB_PRDGRP_ID] = aWBPrdGrpRow
          }
        })
        self._notifyQueueChange()
        self._IHaveEvents_processQueue()

      })
      .catch((err) => {
        console.log(err)
        return
      })
  }

  loadAllProductGroups(): void {
    this._bLoadAllPrdGrpsQueued = true
    this._notifyQueueChange()
    this._IHaveEvents_processQueue()
  }
  get ProductGroupsLoaded(): WB_ProductGroupRow[] {
    return _.values(this._dctPrdGrpRowLoadedById)
  }
  get ProductGroupsAllLoaded(): boolean {
    return this._bLoadAllPrdGrpsLoaded
  }
  get EventLoadProgressProvider(): IProgressProvider {
    return this._EventLoadProgressProvider
  }


  private _IHaveEvents_processQueue(): void {

    let self = this
    let bDebugQueueProcessing = true
    let bLoadIteratively = true

    // 0. If Loading paused, exit
    // 1. If Load Running, exit
    // 2. Check if the queue is empty
    // 3. Copy the queue, union with already loaded
    // 4. Determine EventID set load set from union of all queued & loaded
    // 5. Difference Loaded Events to Determined Load List
    // 6. Load Events && Add them to Collection
    // 7. Delete loaded items from the queue, Update Loaded List
    // 8. Notify addition

    if(bDebugQueueProcessing) console.log('IHaveEvents_processQueue started')

    // 0. If Loading Paused, exit
    if (self._bQueueLoadProcessPaused) {
      console.log('IHaveEvents_processQueue ended: paused')
      return
    }

    // 1. If Load Running, exit
    if (self._bQueueLoadProcessRunning) {
      console.log('IHaveEvents_processQueue ended: already running')
      return
    }

    // 2. Check if the queue is empty
      if (_.isEmpty(self._dctPlanBOQueuedById)
          && _.isEmpty(self._dctPrdGrpRowQueuedById)
          && _.isEmpty(self._dctEventRowQueuedById)
          && (self._bLoadAllPrdGrpsQueued == self._bLoadAllPrdGrpsLoaded) ) {
        console.log('IHaveEvents_processQueue ended: empty queue')
        self._notifyProcessingComplete()
        return
      }

    // Mark running
    self._bQueueLoadProcessRunning = true
    if(bDebugQueueProcessing) console.log('IHaveEvents_processQueue set Queue Running.')
    self._EventLoadProgressProvider.start("Loading Events")

    let failCount = 0
    let successCount  = 0
    let addedEventsBOs: EventBO[] = []


    // 3. Copy the queue, union with already loaded
    let dctPlanByIdCurrentLoad =  _.clone(self._dctPlanBOQueuedById)
    let dctPrdGrpRowByIdCurrentQueueLoad = _.clone(self._dctPrdGrpRowQueuedById)
    let dctEventRowByIdCurrentQueueLoad = _.clone(self._dctEventRowQueuedById)
    let bLoadAllProductGroupCurrentQueueLoad = self._bLoadAllPrdGrpsQueued || self._bLoadAllPrdGrpsLoaded

    _.forEach(self._dctPlanBOLoadedById, (aPlanBO: PlanBO) => {
      if (!(aPlanBO.drWB_Plan.cif.WB_PLAN_ID in dctPlanByIdCurrentLoad)) {
        dctPlanByIdCurrentLoad[aPlanBO.drWB_Plan.cif.WB_PLAN_ID] = aPlanBO
      }
    })

    _.forEach(self._dctPrdGrpRowLoadedById, (aPrdGrpRow: WB_ProductGroupRow) => {
      if (!(aPrdGrpRow.cif.WB_PRDGRP_ID in dctPrdGrpRowByIdCurrentQueueLoad)) {
        dctPrdGrpRowByIdCurrentQueueLoad[aPrdGrpRow.cif.WB_PRDGRP_ID] = aPrdGrpRow
      }
    })

    _.forEach(self._dctEventRowLoadedById, (anEventRow: WB_EventRow) => {
      if (!(anEventRow.cif.WB_EVT_ID in dctEventRowByIdCurrentQueueLoad)) {
        dctEventRowByIdCurrentQueueLoad[anEventRow.cif.WB_EVT_ID] = anEventRow
      }
    })

    let arrWBPlanIds = _.map(dctPlanByIdCurrentLoad,(aPlanBO: PlanBO) => aPlanBO.drWB_Plan.cif.WB_PLAN_ID)

    let arrWBPrdGrpIds = _.map(dctPrdGrpRowByIdCurrentQueueLoad,(aPrdGrpRow: WB_ProductGroupRow) => aPrdGrpRow.cif.WB_PRDGRP_ID)

    let arrWBEventIds = _.map(dctEventRowByIdCurrentQueueLoad,(anEventRow: WB_EventRow) => anEventRow.cif.WB_EVT_ID)



    // 4. Determine EventID set load set from union of all queued & loaded

    if (_.isEmpty(arrWBPlanIds)) {
      arrWBPlanIds = [-99999]
    }

    let condition: any = {WB_PLAN_ID: {inq: arrWBPlanIds}}

    if (!bLoadAllProductGroupCurrentQueueLoad) {
      if (_.isEmpty(arrWBPrdGrpIds)) {
        arrWBPrdGrpIds = [-99999]
      }

      condition = {and: [condition, {WB_PRDGRP_ID: {inq: arrWBPrdGrpIds}}]}
    }

    let bDone = false
    let anEventLoader: BusinessObjectLoader<EventBO> = undefined

    if(bDebugQueueProcessing) console.log('Queue Condition: ' + JSON.stringify(condition))
    PASearch.find('vWB_Events_By_Plan_PrdGrp',
       {where: condition},
      {
        WB_PRDGRP_ID: {type: 'number'},
        WB_PLAN_ID: {type: 'number'},
        WB_EVT_ID: {type: 'number'}
      },
      [],
      0,
      ['WB_EVT_ID','WB_PLAN_ID'])
      .then((arrWBEventIdsFound: {WB_EVT_ID: number, WB_PLAN_ID: number}[]) => {
        if(!_.isEmpty(arrWBEventIds)) {
          arrWBEventIdsFound = _.map(dctEventRowByIdCurrentQueueLoad, (anEvtRow: WB_EventRow) => {
            return {WB_EVT_ID: anEvtRow.cif.WB_EVT_ID, WB_PLAN_ID: 0}
          })
        }

        if (!_.isArray(arrWBEventIdsFound)){
          if(bDebugQueueProcessing) console.log('IHaveEvents_processQueue set Queue Running = false, not array.')
          self._bQueueLoadProcessRunning = false
          self._EventLoadProgressProvider.finish()
          self._notifyProcessingComplete()
          bDone = true
          return
        }

        let arrWBEventToLoad: {WB_EVT_ID: number, WB_PLAN_ID: number}[] = []

        // 5. Difference Loaded Events to Determined Load List
        _.forEach(arrWBEventIdsFound, (aRow) => {
          if (!(aRow.WB_EVT_ID in self._dctEventById)) {
            arrWBEventToLoad.push(aRow)
          }
        })

        if (_.isEmpty(arrWBEventToLoad)){
          if(bDebugQueueProcessing) console.log('IHaveEvents_processQueue set Queue Running = false, already loaded.')
          self._bQueueLoadProcessRunning = false
          self._EventLoadProgressProvider.finish()
          self._notifyProcessingComplete()
          return
        }

        self._EventLoadProgressProvider.setProgressDenominator(arrWBEventToLoad.length)

        let dctWBEventRowsByPlanId = _.groupBy(arrWBEventToLoad,'WB_PLAN_ID')

        let tasks: Promise<any>[] = []

        // 6. Load Events && Add them to Collection

        _.forEach(dctWBEventRowsByPlanId, (arrRows, keyWBPlanId) => {
          let arrWBEventIdsToLoad = _.map(arrRows, (aRow) => { return aRow.WB_EVT_ID })
          // Set the Plan in the options (if its there)
          let anEventCreateOptions = _.clone(self._options.EventCreateOptions)
          anEventCreateOptions.dbContext = self.dbContext
          if(!_.isUndefined(dctPlanByIdCurrentLoad[Number(keyWBPlanId)]))
            anEventCreateOptions.Plan = dctPlanByIdCurrentLoad[Number(keyWBPlanId)]

          let anEventLoadOptions = _.clone(self._options.EventLoadOptions)
          anEventLoadOptions.dbContext = self.dbContext
          if(!_.isUndefined(dctPlanByIdCurrentLoad[Number(keyWBPlanId)]))
            anEventLoadOptions.Plan = dctPlanByIdCurrentLoad[Number(keyWBPlanId)]

          anEventLoader = new BusinessObjectLoader<EventBO>(EventBO, anEventCreateOptions, arrWBEventIdsToLoad, 1)
          anEventLoader.LoadProgressProvider.addListener(self.EventLoadProgressProvider)

          anEventLoader.cbOnEachLoadComplete = (anEventBO: EventBO) => {
            if(bDebugQueueProcessing){
              console.log("Loaded Event: " + anEventBO.drWB_Event.cif.EVENT_NAME)
            }
            if(bLoadIteratively) {
              successCount += 1
              if (!(anEventBO.drWB_Event.cif.WB_EVT_ID in self._dctEventById)) {
                addedEventsBOs.push(anEventBO)
                self._dctEventById[anEventBO.drWB_Event.cif.WB_EVT_ID] = anEventBO
              }
              self._notifyChange('add', [anEventBO], {})
              self._EventLoadProgressProvider.setProgressMessage("EventLoader", "Loaded " + anEventBO.drWB_Event.cif.EVENT_ID)
              console.log("EventLoadProgressProvider numerator: ", successCount + failCount)
              self._EventLoadProgressProvider.setProgressNumerator(successCount + failCount)
            }
          }

          anEventLoader.cbOnEachLoadFailed = (aBOId) => {
            if(bDebugQueueProcessing){
              console.log("Failed to load Event: " + aBOId)
            }
            if(bLoadIteratively){
              failCount += 1
              self._EventLoadProgressProvider.setProgressNumerator(successCount + failCount)
            }
          }

          let loadTask = anEventLoader.Load(anEventLoadOptions)
          tasks.push(loadTask)
        })


        return Promise.all(tasks)
      })
      .then((arrEvtLoaders: BusinessObjectLoader<EventBO>[]) => {
        if (bDone) {
          return
        }

        if(anEventLoader)
         anEventLoader.LoadProgressProvider.removeListener(self.EventLoadProgressProvider)

        if (!bLoadIteratively) {
          _.forEach(arrEvtLoaders, (anEvtLoader) => {

            if (anEvtLoader.LoadProgressProvider.process_failed) {
              failCount += 1
              self.EventLoadProgressProvider.exception(anEvtLoader.LoadProgressProvider.exception)
              return
            }
            successCount += 1

            // Load BOs into Collection
            _.forEach(anEvtLoader.LoadedBOs, (anEventBO: EventBO) => {
              if (!(anEventBO.drWB_Event.cif.WB_EVT_ID in self._dctEventById)) {
                addedEventsBOs.push(anEventBO)
                self._dctEventById[anEventBO.drWB_Event.cif.WB_EVT_ID] = anEventBO
              }
            })
          })

          self._EventLoadProgressProvider.setProgressNumerator(successCount + failCount)

        }

        if ((successCount == 0) && (failCount > 0)) {
          if (bDebugQueueProcessing) console.log('IHaveEvents_processQueue set Queue Running = false, loader failed.')
          self._bQueueLoadProcessRunning = false
          self._EventLoadProgressProvider.finish()
          return
        }

        // 7. Delete loaded items from the queue, Update Loaded List
        // dctPlanByIdCurrentLoad
        // dctPrdGrpRowByIdCurrentQueueLoad
        // bLoadAllProductGroupCurrentQueueLoad

        _.forEach(dctPlanByIdCurrentLoad, (aPlanBO: PlanBO) => {
          // Remove Plans from Queue
          if (aPlanBO.drWB_Plan.cif.WB_PLAN_ID in self._dctPlanBOQueuedById) {
            delete self._dctPlanBOQueuedById[aPlanBO.drWB_Plan.cif.WB_PLAN_ID]
          }

          // Add to Loaded
          if (!(aPlanBO.drWB_Plan.cif.WB_PLAN_ID in self._dctPlanBOLoadedById)) {
            self._dctPlanBOLoadedById[aPlanBO.drWB_Plan.cif.WB_PLAN_ID] = aPlanBO
          }
        })

        _.forEach(dctPrdGrpRowByIdCurrentQueueLoad, (aWBPrdGrpRow: WB_ProductGroupRow) => {
          // Remove PrdGrps from Queue
          if (aWBPrdGrpRow.cif.WB_PRDGRP_ID in self._dctPrdGrpRowQueuedById) {
            delete self._dctPrdGrpRowQueuedById[aWBPrdGrpRow.cif.WB_PRDGRP_ID]
          }

          // Add to Loaded
          if (!(aWBPrdGrpRow.cif.WB_PRDGRP_ID in self._dctPrdGrpRowLoadedById)) {
            self._dctPrdGrpRowLoadedById[aWBPrdGrpRow.cif.WB_PRDGRP_ID] = aWBPrdGrpRow
          }
        })

        _.forEach(dctEventRowByIdCurrentQueueLoad, (anEventRow: WB_EventRow) => {
          // Remove Events from Queue
          if (anEventRow.cif.WB_EVT_ID in self._dctEventRowQueuedById) {
            delete self._dctEventRowQueuedById[anEventRow.cif.WB_EVT_ID]
          }

          // Add to Loaded
          if (!(anEventRow.cif.WB_EVT_ID in self._dctEventRowLoadedById)) {
            self._dctEventRowLoadedById[anEventRow.cif.WB_EVT_ID] = anEventRow
          }
        })

        self._bLoadAllPrdGrpsLoaded = bLoadAllProductGroupCurrentQueueLoad

        if (bDebugQueueProcessing) console.log('IHaveEvents_processQueue set Queue Running = false, done.')
        self._bQueueLoadProcessRunning = false

        self._EventLoadProgressProvider.finish()


        self._notifyQueueChange()
        self._notifyProcessingComplete()

        // 8. Notify addition
        if (!bLoadIteratively) {
          self._notifyChange('add', addedEventsBOs, {})
        }

        setTimeout(function(){
          self._IHaveEvents_processQueue()
        }, 0)

      })
      .catch((err) => {
        console.log('IHaveEvents_processQueue set Queue Running = false, error.')
        self._bQueueLoadProcessRunning = false
        self._EventLoadProgressProvider.exception(err)
        self._notifyProcessingComplete()
      })

  }


  // Queue Functions

  getEventIds(bIncludePGFilter?: boolean): Promise<number[]> {
    const self = this

    return new Promise<number[]>((resolve, error) => {

      // Check if the queue is empty
      if (_.isEmpty(self._dctPlanBOQueuedById)
        && _.isEmpty(self._dctPrdGrpRowQueuedById)
        && (self._bLoadAllPrdGrpsQueued == self._bLoadAllPrdGrpsLoaded)) {
        resolve([])
        return
      }

      // The following logic to determine the condition is the same as the processQueue logic..

      // Copy the queue, union with already loaded
      let dctPlanByIdCurrentLoad = _.clone(self._dctPlanBOQueuedById)
      let dctPrdGrpRowByIdCurrentQueueLoad = _.clone(self._dctPrdGrpRowQueuedById)
      let bLoadAllProductGroupCurrentQueueLoad = self._bLoadAllPrdGrpsQueued || self._bLoadAllPrdGrpsLoaded

      _.forEach(self._dctPlanBOLoadedById, (aPlanBO: PlanBO) => {
        if (!(aPlanBO.drWB_Plan.cif.WB_PLAN_ID in dctPlanByIdCurrentLoad)) {
          dctPlanByIdCurrentLoad[aPlanBO.drWB_Plan.cif.WB_PLAN_ID] = aPlanBO
        }
      })

      _.forEach(self._dctPrdGrpRowLoadedById, (aPrdGrpRow: WB_ProductGroupRow) => {
        if (!(aPrdGrpRow.cif.WB_PRDGRP_ID in dctPrdGrpRowByIdCurrentQueueLoad)) {
          dctPrdGrpRowByIdCurrentQueueLoad[aPrdGrpRow.cif.WB_PRDGRP_ID] = aPrdGrpRow
        }
      })

      let arrWBPlanIds = _.map(dctPlanByIdCurrentLoad, (aPlanBO: PlanBO) => {
        return aPlanBO.drWB_Plan.cif.WB_PLAN_ID
      })

      let arrWBPrdGrpIds = _.map(dctPrdGrpRowByIdCurrentQueueLoad, (aPrdGrpRow: WB_ProductGroupRow) => {
        return aPrdGrpRow.cif.WB_PRDGRP_ID
      })


      if (_.isEmpty(arrWBPlanIds)) {
        arrWBPlanIds = [-99999]
      }

      let condition: any = {WB_PLAN_ID: {inq: arrWBPlanIds}}

      if ((!bLoadAllProductGroupCurrentQueueLoad) && bIncludePGFilter) {
        if (_.isEmpty(arrWBPrdGrpIds)) {
          arrWBPrdGrpIds = [-99999]
        }

        condition = {and: [condition, {WB_PRDGRP_ID: {inq: arrWBPrdGrpIds}}]}
      }

      PASearch.find('vWB_Events_By_Plan_PrdGrp',
        {where: condition},
        {
          WB_PRDGRP_ID: {type: 'number'},
          WB_PLAN_ID: {type: 'number'},
          WB_EVT_ID: {type: 'number'}
        },
        [],
        0,
        ['WB_EVT_ID', 'WB_PLAN_ID'])
        .then((arrWBEventIdsFound: { WB_EVT_ID: number, WB_PLAN_ID: number }[]) => {
          if (!_.isArray(arrWBEventIdsFound)) {
            resolve([])
          }
          let returnValue: number[] = []
          _.forEach(arrWBEventIdsFound, (aRow) => {
            returnValue.push(aRow.WB_EVT_ID)
          })
          resolve(returnValue)
        })
        .catch((err) => {
          error(err)
        })

    })

  }


  get QueueChanged(): BehaviorSubject<IHaveEvents> {
    return this._QueueChanged
  }

  getPlanBOs(bIncludeQueued?: boolean): PlanBO[] {
    if(bIncludeQueued){
      return _.union(_.values(this._dctPlanBOQueuedById), _.values(this._dctPlanBOLoadedById))
    } else {
      return _.values(this._dctPlanBOLoadedById)
    }
  }

  getPlans(bIncludeQueued?: boolean): WB_PlanRow[] {
    return _.map(this.getPlanBOs(bIncludeQueued), (aPlanBO: PlanBO) => { return aPlanBO.WB_Plan })
  }

  getProductGroupAll(bIncludeQueued?: boolean): boolean {
    if(bIncludeQueued){
      return this._bLoadAllPrdGrpsQueued || this._bLoadAllPrdGrpsLoaded
    } else {
      return this._bLoadAllPrdGrpsLoaded
    }
  }

  getProductGroups(bIncludeQueued?: boolean): WB_ProductGroupRow[] {
    if(bIncludeQueued){
      return _.union(_.values(this._dctPrdGrpRowQueuedById),_.values(this._dctPrdGrpRowLoadedById))
    } else {
      return _.values(this._dctPrdGrpRowLoadedById)
    }
  }

  releaseReference():void{
    for (let eventBO of this.Events){
      eventBO.RefCounter.releaseReference()
    }
  }

  private _notifyChange(type: string, bos: EventBO[], opts: any) {
    _.forEach(this._listeners, (aListener: IHaveEventsListenerFunction) => {
      if (aListener) {
        aListener(this, type, bos, opts)
      }
    })
    this._EventsChanged.next(this)
  }

  private _notifyQueueChange() {
    this._QueueChanged.next(this)
  }

  private _notifyProcessingComplete() {
    const self = this

    _.forEach(self._arrLoadProcessingCompleteFunction, (aCompleteFunction) => {
      if(_.isFunction(aCompleteFunction)){
        aCompleteFunction()
      }
    })

    self._arrLoadProcessingCompleteFunction = []

  }



//</editor-fold>

}

